import { type ModalProps } from '~/util';
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetFooter,
    SheetHeader,
    SheetTitle,
} from '~/primitives/sheet';
import { useAppForm } from '~/util/hooks/useAppForm';
import { formOptions } from '@tanstack/react-form';
import { useList } from '~/util/hooks/useList';
import { moveLevelMutation } from '~/util/mutations/staff/levels';
import { useApiMutation } from '~/util/hooks/useApiMutation';
import { levelMoveFormInput, levelMoveFormSchema } from '~/util/forms/level';
import { getRouteApi } from '@tanstack/react-router';
import { levelsQuery } from '~/util/queries/public/levels';
import { useHydratedQuery } from '~/util/hooks/useHydratedQuery';

import { useEffect, useMemo } from 'react';
import { ListEditPreview, NewLevelPosition } from '../-ListEditPreview';
import { levelStatuses } from '~/util/types/levels';

const Route = getRouteApi('/list/$levelid');

export const MoveLevelSheet: React.FC<ModalProps> = (modalProps) => {
    const { levelid } = Route.useParams();
    const list = useList();
    const { data: levels = [] } = useHydratedQuery(levelsQuery({ list }));
    const mainList = levels.filter((lvl) => lvl.status == 'MainList');

    const mutation = useApiMutation({
        ...moveLevelMutation(),
        onSuccess: () => modalProps.onOpenChange?.(false),
    });

    const level = Route.useLoaderData();

    const defaultMove = useMemo(
        () =>
            level.status == 'Legacy'
                ? {
                      position: mainList.length + 1,
                      status: 'MainList' as const,
                  }
                : {
                      position: level.position,
                      status: level.status,
                  },
        [level.status, level.position, mainList.length],
    );

    const maxMovePositionOnMainList =
        mainList.length + (level.status != 'MainList' ? 1 : 0);

    const form = useAppForm(
        formOptions({
            formId: 'move-level-staff',
            defaultValues: {
                list,
                level: defaultMove,
            } as levelMoveFormInput,
            validators: {
                onChangeAsyncDebounceMs: 500,
                onChange: levelMoveFormSchema
                    .refine(
                        // also validate position if it's out of bounds of the main list
                        (data) => {
                            const status = data.level.status;
                            const position =
                                data.level.position ?? level.position;
                            return (
                                status != 'MainList' ||
                                (position != null &&
                                    position >= 1 &&
                                    position <= maxMovePositionOnMainList)
                            );
                        },
                        {
                            path: ['level', 'position'],
                            error: `Invalid position. (1-${maxMovePositionOnMainList} allowed)`,
                        },
                    )
                    .refine(
                        // don't move if nothing changed
                        (data) => {
                            return (
                                (level.status !== data.level.status ||
                                    level.position !== data.level.position) &&
                                !(
                                    level.status == 'Legacy' && // don't move from legacy to legacy (theoretically supported but no real need to)
                                    data.level.status == 'Legacy'
                                )
                            );
                        },
                    ),
            },
            onSubmit: async ({ value }) => {
                mutation.mutate({
                    data: value,
                    levelid,
                });
            },
        }),
    );

    useEffect(() => {
        form.reset({ list, level: defaultMove });
    }, [list, form, defaultMove]);

    const getPostMoveLevels = (
        values: levelMoveFormInput,
    ): { above: NewLevelPosition | null; below: NewLevelPosition | null } => {
        const { position } = values.level;
        if (!position || !levels.length) return { above: null, below: null };

        // simulate removing the current level from the main list and look at the levels above and below the target position
        const newList = [...levels];
        if (level.position != null) newList.splice(level.position - 1, 1);

        const above = newList[position - 2] ?? null;
        const below = newList[position - 1] ?? null;

        return {
            above: above
                ? {
                      ...above,
                      position: position - 1,
                  }
                : null,
            below: below
                ? {
                      ...below,
                      position: position + 1,
                  }
                : null,
        };
    };

    return (
        <>
            <Sheet {...modalProps}>
                <SheetContent className="w-full sm:w-auto sm:min-w-150 sm:max-w-150 overflow-y-scroll">
                    <SheetHeader>
                        <SheetTitle>Move level</SheetTitle>
                        <SheetDescription>
                            Change this level&apos;s status or position on the
                            list. Can be used to place pending levels, to move
                            levels on the list or in/out of the legacy list, or
                            to remove levels from the list. Note that moving a
                            level to the legacy list will always move it at the
                            top of it, and that removing a level will only hide
                            it but not delete it.
                        </SheetDescription>
                    </SheetHeader>
                    <form
                        id="move-level-staff"
                        onSubmit={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            form.handleSubmit();
                        }}
                    >
                        <div className="flex flex-col justify-start items-start gap-x-6 gap-y-3 p-6 pt-6">
                            <form.Subscribe
                                selector={(state) => state.values.level.status}
                            >
                                {(status) =>
                                    (status == 'MainList' ||
                                        status == 'Legacy') && (
                                        <form.AppField name="level.position">
                                            {(field) => (
                                                <field.NumberField
                                                    label="New Position"
                                                    required
                                                    placeholder="1000"
                                                    disabled={
                                                        status == 'Legacy'
                                                    }
                                                />
                                            )}
                                        </form.AppField>
                                    )
                                }
                            </form.Subscribe>

                            <form.AppField
                                name="level.status"
                                listeners={{
                                    onChange: ({ value }) => {
                                        form.setFieldValue(
                                            'level.position',
                                            value == 'Pending' ||
                                                value == 'Removed'
                                                ? null
                                                : level.status == 'MainList' &&
                                                    value == 'Legacy'
                                                  ? mainList.length
                                                  : defaultMove.position,
                                        );
                                    },
                                }}
                            >
                                {(field) => (
                                    <field.DropdownField
                                        label="Status"
                                        classname="w-fit"
                                        options={levelStatuses}
                                        required
                                        labelNotes="Levels set as Pending/Removed do not have a given position. MainList/Legacy ones must have one."
                                        placeholder="Select status"
                                    />
                                )}
                            </form.AppField>
                        </div>

                        <form.Subscribe>
                            {({ values }) => {
                                if (
                                    !values.level.position ||
                                    values.level.position == level.position
                                )
                                    return null;

                                const { above, below } =
                                    getPostMoveLevels(values);

                                return (
                                    <>
                                        <h3 className="font-semibold w-fit mx-auto mb-2">
                                            Move preview:
                                        </h3>
                                        <ListEditPreview
                                            above={above}
                                            middle={{
                                                name: level.name,
                                                position:
                                                    values.level.position ??
                                                    level.position,
                                                status:
                                                    values.level.status ??
                                                    level.status,
                                            }}
                                            below={below}
                                        />
                                    </>
                                );
                            }}
                        </form.Subscribe>
                        <SheetFooter className="sm:justify-center">
                            <form.AppForm>
                                <form.SubscribeButton
                                    label="Move level"
                                    classname="w-11/12"
                                />
                            </form.AppForm>
                        </SheetFooter>
                    </form>
                </SheetContent>
            </Sheet>
        </>
    );
};
